if (!customElements.get('variant-selects')) {
  /** Same rules as ProductInfo.getVariantPickerOptionGroups — variant-selects uses display:contents. */
  function getVariantPickerOptionGroups(variantSelects) {
    if (!variantSelects) return [];
    const isGroup = (el) => el?.matches?.('fieldset.product-form__input, div.product-form__input');

    const direct = Array.from(variantSelects.children).filter(isGroup);
    if (direct.length) return direct;

    const scoped = variantSelects.querySelectorAll(
      ':scope > fieldset.product-form__input, :scope > div.product-form__input'
    );
    if (scoped.length) return Array.from(scoped);

    const flat = Array.from(
      variantSelects.querySelectorAll('fieldset.product-form__input, div.product-form__input')
    );
    const byParent = flat.filter((el) => el.parentElement === variantSelects);
    if (byParent.length) return byParent;

    return flat;
  }

  function syncSwatchLabelsFromCheckedInputs(variantSelects) {
    getVariantPickerOptionGroups(variantSelects).forEach((group) => {
      const input = group.querySelector(
        'input[type="radio"]:checked, input[type="checkbox"]:checked, .swatch-input__input:checked'
      );
      if (!input) return;
      const labelEl =
        group.querySelector('[data-selected-swatch-value]') || group.querySelector('[data-selected-value]');
      if (labelEl) labelEl.textContent = input.value;
    });
  }

  /**
   * Only the **Condition** option row uses combination-based crosses (depends on color & other picks).
   * Color/swatch/other options keep Liquid-rendered disabled state — we do not toggle those inputs here.
   * Matches theme purchasable rule: variant.available && variant.price > 0.
   */
  function getOptionPositionForGroup(group) {
    const raw = group.getAttribute('data-option-position');
    if (raw != null && raw !== '') {
      const p = parseInt(raw, 10);
      if (!Number.isNaN(p) && p >= 1 && p <= 3) return p;
    }
    const first = group.querySelector('input[type="radio"], .swatch-input__input, select');
    if (!first?.name) return null;
    const m = String(first.name).match(/-(\d+)$/);
    return m ? parseInt(m[1], 10) : null;
  }

  function normOpt(s) {
    if (s == null || s === '') return '';
    return String(s).trim();
  }

  function collapseTitle(s) {
    return normOpt(s).replace(/\s+/g, ' ');
  }

  function findVariantForPicks(variants, picks, numOptions) {
    let found = variants.find((v) => {
      if (numOptions >= 1 && picks[0] != null && normOpt(v.option1) !== normOpt(picks[0])) return false;
      if (numOptions >= 2 && picks[1] != null && normOpt(v.option2) !== normOpt(picks[1])) return false;
      if (numOptions >= 3 && picks[2] != null && normOpt(v.option3) !== normOpt(picks[2])) return false;
      return true;
    });

    if (!found && picks.some((p) => p != null && p !== '')) {
      const joined = picks.filter((p) => p != null && p !== '').map((p) => collapseTitle(p)).join(' / ');
      found = variants.find((v) => collapseTitle(v.title) === joined);
    }
    return found || null;
  }

  function isPurchasableVariant(v) {
    return v && v.available && Number(v.price) > 0;
  }

  function syncOptionAvailabilityFromVariants(variantSelects) {
    const productInfo = variantSelects.closest('product-info');
    const variantsEl = productInfo?.querySelector('script[data-product-variants-json]');
    if (!variantsEl) return;

    let variants;
    try {
      variants = JSON.parse(variantsEl.textContent || '[]');
    } catch (e) {
      return;
    }

    const groups = getVariantPickerOptionGroups(variantSelects);
    if (!groups.length) return;

    const numOptions = groups.length;
    /** @type {(string | null)[]} */
    const selectedByPos = [null, null, null];

    groups.forEach((group) => {
      const pos = getOptionPositionForGroup(group);
      if (pos == null || pos < 1 || pos > 3) return;

      const select = group.querySelector('select');
      if (select?.selectedOptions?.[0]) {
        selectedByPos[pos - 1] = String(select.selectedOptions[0].value).trim();
        return;
      }
      const checked = group.querySelector(
        'input[type="radio"]:checked, input[type="checkbox"]:checked, .swatch-input__input:checked'
      );
      if (checked) selectedByPos[pos - 1] = String(checked.value).trim();
    });

    groups.forEach((group) => {
      const pos = getOptionPositionForGroup(group);
      if (pos == null || pos < 1 || pos > 3) return;

      const optionNameDown = (group.getAttribute('data-option-name') || '').trim().toLowerCase();
      if (optionNameDown !== 'condition') return;

      const select = group.querySelector('select');
      if (select) {
        Array.from(select.options).forEach((opt) => {
          const candidate = [...selectedByPos];
          candidate[pos - 1] = String(opt.value).trim();
          const v = findVariantForPicks(variants, candidate, numOptions);
          const ok = isPurchasableVariant(v);
          opt.classList.toggle('disabled', !ok);
        });
        return;
      }

      const inputs = group.querySelectorAll('input[type="radio"], input.swatch-input__input');
      inputs.forEach((input) => {
        const candidate = [...selectedByPos];
        candidate[pos - 1] = String(input.value).trim();
        const v = findVariantForPicks(variants, candidate, numOptions);
        const ok = isPurchasableVariant(v);
        input.classList.toggle('disabled', !ok);
      });
    });

    hideNonexistentOptions(variantSelects, groups, selectedByPos, variants, numOptions);
  }

  /**
   * Hide option values that can't be bought: $0 placeholders (the store creates every combination
   * and prices the ones it doesn't sell at $0) and sold-out variants alike — same purchasable rule
   * as the Add to Cart button (isPurchasableVariant). Never hides the current pick; values linking
   * to another product (combined listings) are left as rendered.
   *  - Button/dropdown rows: per combination with the other current picks.
   *  - Colour swatches: only when no variant of that colour can be bought in any combination, so a
   *    buyable colour never disappears just because of the current condition.
   */
  function hideNonexistentOptions(variantSelects, groups, selectedByPos, variants, numOptions) {
    const productUrl = variantSelects.dataset.url || '';
    const isOtherProductValue = (el) => !!el.dataset?.productUrl && el.dataset.productUrl !== productUrl;
    const isOffered = isPurchasableVariant;
    const exists = (pos, value) => {
      const candidate = [...selectedByPos];
      candidate[pos - 1] = String(value).trim();
      return isOffered(findVariantForPicks(variants, candidate, numOptions));
    };
    const existsInAnyCombination = (pos, value) =>
      variants.some((v) => normOpt(v[`option${pos}`]) === normOpt(value) && isOffered(v));

    groups.forEach((group) => {
      const pos = getOptionPositionForGroup(group);
      if (pos == null || pos < 1 || pos > 3) return;

      if (group.matches('.product-form__input--color, .product-form__input--swatch')) {
        group.querySelectorAll('input[type="radio"], input.swatch-input__input').forEach((input) => {
          if (isOtherProductValue(input)) return;
          const label = input.id ? group.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
          label?.classList.toggle('is-nonexistent', !input.checked && !existsInAnyCombination(pos, input.value));
        });
        return;
      }

      const select = group.querySelector('select');
      if (select) {
        Array.from(select.options).forEach((opt) => {
          if (isOtherProductValue(opt)) return;
          opt.hidden = !opt.selected && !exists(pos, opt.value);
        });
        return;
      }

      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (isOtherProductValue(input)) return;
        const item = input.closest('.variant-option-button-item');
        if (!item) return;
        item.classList.toggle('is-nonexistent', !input.checked && !exists(pos, input.value));
      });
    });
  }

  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this._variantChangeUnsubscriber = undefined;
      }

      syncOptionAvailabilityFromVariants() {
        syncOptionAvailabilityFromVariants(this);
      }

      get selectedOptionValues() {
        return getVariantPickerOptionGroups(this).map((group) => {
          const select = group.querySelector('select');
          if (select) {
            const opt = select.selectedOptions[0];
            return opt?.dataset?.optionValueId;
          }
          const input = group.querySelector(
            'input[type="radio"]:checked, input[type="checkbox"]:checked, .swatch-input__input:checked'
          );
          return input?.dataset?.optionValueId;
        });
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        queueMicrotask(() => {
          syncSwatchLabelsFromCheckedInputs(this);
          syncOptionAvailabilityFromVariants(this);
        });

        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);
          queueMicrotask(() => syncOptionAvailabilityFromVariants(this));
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });

        this._variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            const pi = this.closest('product-info');
            const evtId = event?.data?.sectionId != null ? String(event.data.sectionId) : '';
            const myId = pi?.dataset?.section != null ? String(pi.dataset.section) : '';
            if (!pi || evtId !== myId) return;
            queueMicrotask(() => syncOptionAvailabilityFromVariants(this));
          }
        );
      }

      disconnectedCallback() {
        if (typeof this._variantChangeUnsubscriber === 'function') {
          this._variantChangeUnsubscriber();
          this._variantChangeUnsubscriber = undefined;
        }
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const group = target.closest('.product-form__input');
          if (!group) return;
          const selectedSwatchValue =
            group.querySelector('[data-selected-swatch-value]') || group.querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.textContent = value;
        }
      }
    }
  );
}
